import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';
import moment from "moment";
import { LayoutBody, LayoutContent, LayoutContentTitle } from 'shared/layout';
import { UiAvatar, UiButton, UiDataBoundary, UiNavLink, UiUser, UiIcon } from 'shared/uikit';
import { UserModel } from 'shared/models';
import { useMedia, useNavigate, useRouteParams, useStore } from "shared/hooks";
import { chatRoomsCreateQuery, usersGetQuery } from "shared/queries";
import { ROUTES, ICONS } from "shared/constants";
import { ApplicationModule } from "shared/modules";
import { Router } from 'shared/utilities';
import { PProfileUnavailable } from "pages/profile/components/PProfileUnavailable";
import { PProfileTinets } from "pages/profile/components/PProfileTinets";
import { PProfileLeague } from "pages/profile/components/PProfileLeague";
import './styles.scss';

const userImageDefault: string = require('./assets/user.svg').default;

export const UserPage = observer(() => {
    const navigate = useNavigate();
    const { id } = useRouteParams<{ id: string | number }>();
    const store = useStore(() => ({
        user: new UserModel(),
        isSuccess: true,
        isLoading: true,
        isOpen: false,
    }));

    useEffect(() => {
        ApplicationModule.setTitle('...');
        window.scrollTo(0, 0);
    }, [id]);

    useEffect(() => {
        (async () => {
            if (+store.user.id === +id || store.user.slug === id) {
                ApplicationModule.setTitle(store.user.previewName);
                return;
            }
            store.set('isLoading', true);
            store.set('isSuccess', true);
            const { isSuccess, data } = await usersGetQuery({ id });

            if (isSuccess && data) {
                store.set('user', new UserModel(data.item));
            }

            store.set('isSuccess', isSuccess && data);

            store.set('isLoading', false);
            navigate(ROUTES.USER(store.user.slug), {}, {
                replace: true,
            });
            ApplicationModule.setTitle(store.user.previewName);
        })();
    }, [id, store, navigate]);

    const { is1024, is768, is320, isMobileOrTablet } = useMedia();

    const handleChatRoomCreate = useCallback(async () => {
        const { isSuccess, data } = await chatRoomsCreateQuery({
            userId: store.user.id,
            typeId: 'direct'
        });
        if (isSuccess && data) {
            navigate(ROUTES.CHAT(data.item.id));
        }
    }, [navigate, store]);

    const showSection = store.user.responsibilities || (store.user.management.length > 0) || (store.user.colleagues.length > 0);

    const isWidgetsLeague = () => {
        return store.user.tinetsWidgets?.find(item => item.typeId === "league_rating") ? true : false;
    }

    const linkBack = () => {
        if (store.user.departmentBranch) {
            if (store.user.departmentBranch?.department?.isVisible) {
                return ROUTES.DEPARTMENT(store.user.departmentBranch?.department?.id);
            }

            if (store.user.departmentBranch.isVisible) {
                return ROUTES.DEPARTMENT_BRANCH(store.user.departmentBranch.id);
            }
        }

        return ROUTES.COMPANY_STRUCTURE();
    }

    return (
        <LayoutBody>
            <LayoutContent theme={"transparent"}>
                <UiDataBoundary className='p-user' isLoading={store.isLoading} isError={!store.isSuccess}>
                    <div className="p-user__top">
                        <UiNavLink className="p-user__back" to={linkBack()}>
                            <UiIcon
                                size={32}
                                icon={ICONS["BACK_ICON"]}
                            />
                        </UiNavLink>

                        <div className="p-user__group">
                            <div className="p-user__name">{ store.user.previewName }</div>
                            <div className="p-user__depatment">
                                <div className="p-user__depatment-list">
                                    <div className="p-user__depatment-item">
                                        {store.user.userPositionName}
                                        {store.user.hiredAt ? `, стаж ${moment(store.user.hiredAt).fromNow(true)}` : ''}
                                    </div>
                                    {store.user.departmentBranch && (
                                        store.user.departmentBranch.isVisible && (
                                            <UiNavLink
                                                to={ROUTES.DEPARTMENT_BRANCH(store.user.departmentBranch.id)}
                                                className="p-user__depatment-item p-user__depatment-item--link"
                                            >
                                                {store.user.departmentBranch?.name}
                                            </UiNavLink>
                                        )
                                    )}
                                    {store.user.departmentBranch && (
                                        store.user.departmentBranch?.department?.isVisible && (
                                            <UiNavLink
                                                to={ROUTES.DEPARTMENT(store.user.departmentBranch?.department?.id)}
                                                className="p-user__depatment-item p-user__depatment-item--link"
                                            >
                                                {store.user.departmentBranch?.department?.name}
                                            </UiNavLink>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-user__wrapper">
                        <div className="p-user__aside">
                            {
                                !store.user.isAvailable && isMobileOrTablet ?
                                    <PProfileUnavailable user={store.user}/>
                                    : ''
                            }

                            <div className="p-user-section p-user-section--aside">
                                <div className='p-user__avatar'>
                                    {
                                        is1024 || is768 ?
                                            <UiAvatar size={350} image={store.user.imageThumbnail || userImageDefault}/>
                                            :
                                            is320 ?
                                                <UiAvatar size={296} image={store.user.imageThumbnail || userImageDefault}/>
                                                :
                                                <UiAvatar size={264} image={store.user.imageThumbnail || userImageDefault}/>
                                    }
                                </div>
                            </div>

                            {
                                !isMobileOrTablet && (
                                    <div className="p-user-section p-user-section--32pt p-user-section--desktop p-user-section--aside">
                                        <div className='p-user-fields'>
                                            {store.user.phone && (
                                                <div className="p-user-fields__field">
                                                    <div className="p-user-fields__name">Добавочный номер</div>
                                                    <div className="p-user-fields__value">{store.user.phone}</div>
                                                </div>
                                            )}
                                            {store.user.mobile && (
                                                <div className="p-user-fields__field">
                                                    <div className="p-user-fields__name">Мобильный телефон</div>
                                                    <div className="p-user-fields__value">{store.user.mobile}</div>
                                                </div>
                                            )}
                                            {store.user.email && (
                                                <div className="p-user-fields__field">
                                                    <div className="p-user-fields__name">Е-mail</div>
                                                    <a
                                                        href={`mailto:${store.user.email}`}
                                                        className="p-user-fields__value p-user-fields__value--email"
                                                    >
                                                        {store.user.email}
                                                    </a>
                                                </div>
                                            )}
                                        </div>

                                        {+store.user.id !== +ApplicationModule.user.id && (
                                            <>
                                                {store.user.isAvailable && (
                                                    <UiButton className='p-user-btn-send' label='Написать' onClick={handleChatRoomCreate}/>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )
                            }

                        </div>
                        <div className="p-user__main">
                            {
                                !store.user.isAvailable && !isMobileOrTablet ?
                                    <PProfileUnavailable user={store.user}/>
                                    : ''
                            }

                            {
                                store.user.tinetsUserStat && !Array.isArray(store.user.tinetsUserStat) ?
                                    <PProfileTinets user={store.user} isProfile={false} />
                                    : ''
                            }

                            {
                                isWidgetsLeague() ?
                                    <PProfileLeague user={store.user} />
                                    : ""
                            }

                            {
                                isMobileOrTablet && (
                                    <div className="p-user-section p-user-section--mobile">
                                        <div className='p-user-fields'>
                                            {store.user.phone && (
                                                <div className="p-user-fields__field">
                                                    <div className="p-user-fields__name">Добавочный номер</div>
                                                    <div className="p-user-fields__value">{store.user.phone}</div>
                                                </div>
                                            )}
                                            {store.user.mobile && (
                                                <div className="p-user-fields__field">
                                                    <div className="p-user-fields__name">Мобильный телефон</div>
                                                    <div className="p-user-fields__value">{store.user.mobile}</div>
                                                </div>
                                            )}
                                            {store.user.email && (
                                                <div className="p-user-fields__field">
                                                    <div className="p-user-fields__name">Е-mail</div>
                                                    <a
                                                        href={`mailto:${store.user.email}`}
                                                        className="p-user-fields__value p-user-fields__value--email"
                                                    >
                                                        {store.user.email}
                                                    </a>
                                                </div>
                                            )}
                                        </div>

                                        {+store.user.id !== +ApplicationModule.user.id && (
                                            <>
                                                {store.user.isAvailable && (
                                                    <UiButton className='p-user-btn-send' label='Написать' onClick={handleChatRoomCreate}/>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )
                            }

                            {showSection && (store.user.management.length > 0 || store.user.colleagues.length > 0) && (
                                <div className="p-user-section">
                                    <div className="p-user-section__title p-user-section__title--32mb">Связанные сотрудники</div>

                                    {store.user.management.length > 0 && (
                                        <>
                                            <div className="p-user-section__subtitle">Руководитель:</div>
                                            <div className="p-user-employees">
                                                {store.user.management.map(user => (
                                                    <UiUser key={user.id} user={user} isManagement isFramed/>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                    {store.user.colleagues.length > 0 && (
                                        <>
                                            <div className="p-user-section__subtitle">Коллеги:</div>
                                            <div className="p-user-employees">
                                                {store.user.colleagues.map(user => (
                                                    <UiUser key={user.id} user={user} isFramed/>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {showSection && store.user.responsibilities && (
                                <div className="p-user-section p-user-section--32pt p-user-section--32pb">
                                    {store.user.responsibilities && (
                                        <>
                                            <div className="p-user-section__title">Должностные обязанности:</div>
                                            <div className="p-user__responsibilities">{store.user.responsibilities}</div>
                                        </>
                                    )}
                                </div>
                            )}

                            {store.user.birthdayAt && (
                                <div className="p-user-section p-user-section--32pt p-user-section--32pb">
                                    <div className="p-user-section__title">День рождения</div>
                                    <div className="p-user-section__description p-user-section__description--flex">
                                        <div className="p-user-section__birth-icon"></div>
                                        {moment(store.user.birthdayAt).format('D MMMM')}
                                    </div>
                                </div>
                            )}

                            {store.user.userPersonalFields.length > 0 && (
                                <div className="p-user-section p-user-section--32pt p-user-section--32pb">
                                    <div className="p-user-section__title p-user-section__title--40mb">Личная информация:</div>
                                    {store.user.userPersonalFields.map(personalField => {
                                        if (!personalField.value) {
                                            return null;
                                        }
                                        return (
                                            <div key={personalField.id} className="p-user-personal-field">
                                                <div className="p-user-personal-field__name">{personalField.name}</div>
                                                <div className="p-user-personal-field__value">{personalField.value}</div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </UiDataBoundary>
            </LayoutContent>
        </LayoutBody>
    );
})
